import React, { useEffect, useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import useMountedRef from '@aytm/lib/hooks/useMountedRef'

// styles
import '@aytm/lib/rfd-ui/styles/basic/click-target.scss'

ClickTarget.propTypes = {
  clicked: PropTypes.bool,
  resetOnClickOutside: PropTypes.bool,
  className: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  children: PropTypes.any,
  onOpenChange: PropTypes.func
}
export default function ClickTarget(props) {
  const {
    className,
    children,
    clicked: propsClicked = false,
    resetOnClickOutside = true,
    onFocus,
    onBlur,
    onOpenChange,
    ...restProps
  } = props

  const rootElement = useRef(null)
  const oldClickedState = useRef(null)
  const [clicked, setClicked] = useState(propsClicked)
  const mountedRef = useMountedRef()

  // sync props to state
  useEffect(() => {
    setClicked(propsClicked)
  }, [propsClicked])

  useEffect(() => {
    if (onOpenChange && oldClickedState.current !== clicked) {
      onOpenChange(clicked)
      oldClickedState.current = clicked
    }
  }, [clicked, onOpenChange])

  // focus
  const onFocusRef = useRef()
  onFocusRef.current = onFocus
  const onBlurRef = useRef()
  onBlurRef.current = onBlur
  const updateClicked = useCallback(
    newState => {
      if (mountedRef.current && newState !== clicked) {
        setClicked(newState)
        if (newState && onFocusRef.current) {
          onFocusRef.current()
        } else if (!newState && onBlurRef.current) {
          onBlurRef.current()
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clicked]
  )
  const updateClickedRef = useRef()
  updateClickedRef.current = updateClicked

  // click outside
  useEffect(() => {
    if (resetOnClickOutside && clicked) {
      const clickOutside = event => {
        if (null == rootElement.current || null == event.target || !rootElement.current.contains(event.target)) {
          updateClickedRef.current(false)
        }
      }
      document.addEventListener('touchend', clickOutside, true)
      document.addEventListener('mousedown', clickOutside, true)
      return () => {
        document.removeEventListener('touchend', clickOutside, true)
        document.removeEventListener('mousedown', clickOutside, true)
      }
    } else {
      return void 0
    }
  }, [clicked, resetOnClickOutside])

  // Esc closes the target. Same `resetOnClickOutside` gate as the click handler
  // — if the consumer opted out of automatic dismissal, Esc is opt-out too.
  useEffect(() => {
    if (resetOnClickOutside && clicked) {
      const onKeyDown = event => {
        if (event.key === 'Escape') updateClickedRef.current(false)
      }
      document.addEventListener('keydown', onKeyDown, true)
      return () => document.removeEventListener('keydown', onKeyDown, true)
    } else {
      return void 0
    }
  }, [clicked, resetOnClickOutside])

  return (
    <span
      {...restProps}
      className={`rfd-ui-click-target${className ? ' ' + className : ''}`}
      onClick={() => void (!clicked && updateClicked(true))}
      ref={rootElement}
    >
      {children(clicked, updateClicked)}
    </span>
  )
}
